import React, { useState, useEffect } from 'react';
import MainLayout from '../components/Layout/MainLayout';
import { User, Edit, Phone, MapPin, Mail, Bell, Shield, ChevronRight, Settings as SettingsIcon, Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session && user?.email === 'demo@sme.ng') {
        console.log('No session, using Edge Function for demo user');
        
        // Track demo user login
        try {
          await supabase.from('login_history').insert({
            user_id: '11111111-2222-3333-4444-555555555555',
            ip_address: '0.0.0.0',
            user_agent: navigator.userAgent,
            success: true
          });
        } catch (err) {
          console.log('Login tracking error (non-critical):', err);
        }
        
        const response = await fetch(
          `https://sjszhatwjwafioakcncx.supabase.co/functions/v1/get-profile?email=demo@sme.ng`,
          {
            headers: {
              'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNqc3poYXR3andhZmlvYWtjbmN4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTAxMDU5NjAsImV4cCI6MjA2NTY4MTk2MH0.auq6Tek2SaVWjCSt6ew-VQEX21sV0Dx7YJU1vWNTjMA`
            }
          }
        );
        
        if (response.ok) {
          const profileData = await response.json();
          setProfile(profileData);
        } else {
          console.error('Edge Function error:', await response.text());
          setProfile(null);
        }
        return;
      }

      if (!session) {
        console.log('No session, please sign in');
        setProfile(null);
        return;
      }

      const { data: baseUser, error: baseErr } = await supabase
        .from('users')
        .select(`
          first_name,
          last_name,
          email,
          client_number,
          created_at,
          last_login,
          last_login_ip,
          last_login_user_agent,
          login_count
        `)
        .eq('id', user?.id)
        .maybeSingle();

      const { data: ua, error: uaErr } = await supabase
        .from('user_apartments')
        .select(`
          apartment_id,
              apartments (
                id,
                apartment_number,
                buildings (
                  id,
                  name,
                  address
                )
              )
        `)
        .eq('user_id', user?.id)
        .limit(1)
        .maybeSingle();

      console.log('Profile queries:', { baseUser, baseErr, ua, uaErr });

      if (baseErr) {
        console.error('Error fetching base user:', baseErr);
        setProfile(null);
      } else {
        const apartmentData = ua?.apartments ?? null;
        const buildingData = apartmentData?.buildings ?? null;

        setProfile({
          first_name: baseUser?.first_name,
          last_name: baseUser?.last_name,
          email: baseUser?.email,
          client_number: baseUser?.client_number ?? 'Not assigned',
          created_at: baseUser?.created_at,
          last_login: baseUser?.last_login,
          last_login_ip: baseUser?.last_login_ip,
          last_login_user_agent: baseUser?.last_login_user_agent,
          login_count: baseUser?.login_count,
          buildings: buildingData || null,
          apartment: apartmentData || null
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setEditForm({
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({
          first_name: editForm.first_name.trim(),
          last_name: editForm.last_name.trim()
        })
        .eq('id', user.id);

      if (error) throw error;

      toast.success('Profile updated successfully');
      setIsEditDialogOpen(false);
      fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const settingsItems = [
    {
      icon: Bell,
      title: 'Notifications',
      subtitle: 'Manage your notification preferences',
      path: '/user/settings/notifications',
    },
    {
      icon: Shield,
      title: 'Security',
      subtitle: 'Change password and security settings',
      path: '/user/settings/security',
    },
    {
      icon: MapPin,
      title: 'Address',
      subtitle: 'Update your service address',
      path: '/user/settings/address',
    },
    {
      icon: Cookie,
      title: 'Privacy & Data',
      subtitle: 'Manage your data and privacy preferences',
      path: '/user/settings/privacy-data',
    },
  ];

  return (
    <MainLayout title="Profile & Settings">
      <div className="p-4 space-y-6 pb-20">
        {/* Profile Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">
                {loading ? 'Loading...' : profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || user?.email || 'User' : user?.email || 'User'}
              </h2>
              <p className="text-blue-100">Active Member</p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Information</h3>
            <Button variant="outline" size="sm" onClick={handleEditClick}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Email</p>
                <p className="text-gray-600 dark:text-gray-400">{profile?.email || user?.email || 'Not provided'}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Phone className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Client Number</p>
                <p className="text-gray-600 dark:text-gray-400">{profile?.client_number || 'Not assigned'}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <MapPin className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white text-left">Building</p>
                <p className="text-gray-600 dark:text-gray-400">
                  {profile?.buildings ? profile.buildings.name : 'Not assigned'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <MapPin className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white text-left">Apartment</p>
                <p className="text-gray-600 dark:text-gray-400">
                  {profile?.apartment ? `Apartment ${profile.apartment.apartment_number}` : 'Not assigned'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Account Stats */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm text-center">
          <p className="text-2xl font-bold text-blue-500">
            {profile?.created_at ? (() => {
              const diffMs = new Date().getTime() - new Date(profile.created_at).getTime();
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              const diffMonths = Math.floor(diffDays / 30);
              const diffYears = Math.floor(diffDays / 365);
              
              if (diffYears >= 1) return diffYears;
              if (diffMonths >= 1) return diffMonths;
              if (diffDays >= 1) return diffDays;
              return Math.floor(diffMs / (1000 * 60 * 60));
            })() : '8'}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {profile?.created_at ? (() => {
              const diffMs = new Date().getTime() - new Date(profile.created_at).getTime();
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              const diffMonths = Math.floor(diffDays / 30);
              const diffYears = Math.floor(diffDays / 365);
              
              if (diffYears >= 1) return diffYears === 1 ? 'Year with us' : 'Years with us';
              if (diffMonths >= 1) return diffMonths === 1 ? 'Month with us' : 'Months with us';
              if (diffDays >= 1) return diffDays === 1 ? 'Day with us' : 'Days with us';
              return 'Hours with us';
            })() : 'Months with us'}
          </p>
        </div>

        {/* Login Details */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Login Information</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Last Login:</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {profile?.last_login ? new Date(profile.last_login).toLocaleString() : 'Never'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Logins:</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {profile?.login_count || 0}
              </span>
            </div>
            {profile?.last_login_ip && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Last IP:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {profile.last_login_ip}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Settings Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <SettingsIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Settings</h3>
          </div>
          <div className="space-y-1">
            {settingsItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={index}
                  onClick={() => navigate(item.path)}
                  className="w-full flex items-center space-x-4 p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                    <Icon size={20} className="text-gray-600 dark:text-gray-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-white">{item.title}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{item.subtitle}</p>
                  </div>
                  <ChevronRight size={20} className="text-gray-400" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Edit Profile Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Profile</DialogTitle>
              <DialogDescription>
                Update your profile information
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  placeholder="Enter first name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  placeholder="Enter last name"
                />
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveProfile} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default Settings;
